// iCalendar (RFC 5545) builder — dependency-free, plain TypeScript.
//
// Used by the in-app "Add to calendar" download AND copied verbatim into the
// `ics-feed` edge function (Supabase bundles each function's own directory; the
// codebase already copies helpers rather than cross-importing). Keep this file
// free of any runtime imports and of any Node/Deno-only API so the SAME source
// runs in the browser and in Deno. Only `Date`, `Intl`, and `TextEncoder` are
// used — all available in both runtimes.
//
// RECURRENCE STRATEGY: we expand recurring series into discrete one-shot VEVENTs
// (each at a correctly-computed UTC instant) over a bounded window, rather than
// emitting RRULE + VTIMEZONE. This is DST-correct (every occurrence's instant is
// computed against the offset in effect on that date) and avoids generating
// VTIMEZONE blocks for arbitrary IANA zones — the single riskiest part of an ics
// serializer. The trade-off: the feed lists occurrences, not a rule, bounded to
// a rolling horizon (calendars auto-refetch subscriptions). Recurrence presets
// are intentionally limited to trivially-anchorable forms (DAILY / WEEKLY with an
// optional INTERVAL, stepped from DTSTART) so this hand-rolled expander is exact.

export interface IcsAppointment {
  id: string;
  title: string;
  attendeeName?: string;
  attendeeEmail?: string;
  location?: string;
  notes?: string;
  startsAt: string;        // ISO instant
  endsAt: string;          // ISO instant
  timezone: string;        // IANA zone, e.g. 'America/New_York'
  status?: string;         // 'scheduled' | 'completed' | 'canceled' | 'no_show'
  rrule?: string;          // RFC-5545 value, e.g. 'FREQ=WEEKLY;INTERVAL=2'
  exdate?: string[];       // ISO starts of cancelled occurrences
}

export interface Occurrence {
  start: Date;
  end: Date;
}

// ---- low-level text helpers -------------------------------------------------

/** Escape an iCalendar TEXT value (RFC 5545 §3.3.11). Not applied to `:`. */
export function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

const encoder = new TextEncoder();
const byteLen = (s: string): number => encoder.encode(s).length;

/**
 * Fold one content line to ≤75 octets per physical line (RFC 5545 §3.1).
 * Continuation lines begin with a single space (which counts toward the 75).
 * Iterates by code point so a multi-byte character is never split.
 */
export function foldLine(line: string): string {
  if (byteLen(line) <= 75) return line;
  const chunks: string[] = [];
  let cur = '';
  let curBytes = 0;
  let first = true;
  for (const ch of line) {
    const b = byteLen(ch);
    const limit = first ? 75 : 74; // continuation lines spend 1 octet on the leading space
    if (curBytes + b > limit) {
      chunks.push(cur);
      first = false;
      cur = ch;
      curBytes = b;
    } else {
      cur += ch;
      curBytes += b;
    }
  }
  chunks.push(cur);
  return chunks.join('\r\n ');
}

// ---- date / timezone helpers ------------------------------------------------

/** UTC basic format, e.g. 20260612T170000Z. */
export function fmtUtcBasic(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** Minutes east of UTC for `tz` at the instant `d` (e.g. -240 for EDT). */
function offsetMinutes(d: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(d)) p[part.type] = part.value;
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return Math.round((asUTC - d.getTime()) / 60000);
}

/** The wall-clock components of instant `d` as seen in `tz`. */
function localParts(d: Date, tz: string): { y: number; mo: number; day: number; h: number; mi: number } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(d)) p[part.type] = part.value;
  return { y: +p.year, mo: +p.month - 1, day: +p.day, h: +p.hour, mi: +p.minute };
}

/**
 * Inverse of localParts: the UTC instant for a wall-clock time in `tz`.
 * Two-pass to settle DST boundaries (the ambiguous fall-back / non-existent
 * spring-forward hour is approximated — negligible for appointments).
 */
export function zonedTimeToUtc(y: number, mo: number, day: number, h: number, mi: number, tz: string): Date {
  const guess = Date.UTC(y, mo, day, h, mi, 0);
  const off1 = offsetMinutes(new Date(guess), tz);
  let utc = guess - off1 * 60000;
  const off2 = offsetMinutes(new Date(utc), tz);
  if (off2 !== off1) utc = guess - off2 * 60000;
  return new Date(utc);
}

// ---- recurrence expansion ---------------------------------------------------

/**
 * Expand an appointment into its occurrences overlapping [winStart, winEnd].
 * Non-recurring → at most one. Recurring → discrete occurrences preserving the
 * series' local wall-clock time (DST-correct), minus any in `exdate`.
 *
 * Supported RRULE: FREQ=DAILY|WEEKLY with an optional INTERVAL and an optional
 * COUNT or UNTIL. Stepped from DTSTART, so the weekday/time is inherited — no
 * multi-BYDAY anchoring (intentionally, to keep this exact).
 */
export function expandOccurrences(appt: IcsAppointment, winStart: Date, winEnd: Date): Occurrence[] {
  const start = new Date(appt.startsAt);
  const durationMs = new Date(appt.endsAt).getTime() - start.getTime();
  const excluded = new Set((appt.exdate || []).map((x) => new Date(x).getTime()));
  const overlaps = (s: Date): boolean => {
    const e = new Date(s.getTime() + durationMs);
    return e >= winStart && s <= winEnd;
  };

  if (!appt.rrule) {
    if (overlaps(start) && !excluded.has(start.getTime())) {
      return [{ start, end: new Date(start.getTime() + durationMs) }];
    }
    return [];
  }

  const rule: Record<string, string> = {};
  for (const kv of appt.rrule.split(';')) {
    const [k, v] = kv.split('=');
    if (k) rule[k.toUpperCase()] = v;
  }
  const freq = (rule.FREQ || 'WEEKLY').toUpperCase();
  const interval = Math.max(1, parseInt(rule.INTERVAL || '1', 10) || 1);
  const count = rule.COUNT ? parseInt(rule.COUNT, 10) : null;
  const until = rule.UNTIL ? new Date(appt.startsAt) : null; // UNTIL handled below if present
  if (rule.UNTIL && until) {
    // UNTIL is a UTC basic stamp (yyyymmddThhmmssZ); parse to a Date.
    const m = rule.UNTIL.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/);
    if (m) until.setTime(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]));
  }
  const stepDays = freq === 'DAILY' ? interval : 7 * interval; // WEEKLY default

  const lp = localParts(start, appt.timezone);
  const out: Occurrence[] = [];
  const MAX = 1000; // hard safety bound
  for (let k = 0; k < MAX; k++) {
    if (count != null && k >= count) break;
    // Advance the LOCAL calendar date by k*stepDays, keeping wall-clock h:mi,
    // then resolve back to a UTC instant in the appointment's zone.
    const carrier = new Date(Date.UTC(lp.y, lp.mo, lp.day + k * stepDays, lp.h, lp.mi));
    const occStart = zonedTimeToUtc(
      carrier.getUTCFullYear(), carrier.getUTCMonth(), carrier.getUTCDate(),
      carrier.getUTCHours(), carrier.getUTCMinutes(), appt.timezone,
    );
    if (until && occStart.getTime() > until.getTime()) break;
    if (occStart.getTime() > winEnd.getTime()) break;
    if (overlaps(occStart) && !excluded.has(occStart.getTime())) {
      out.push({ start: occStart, end: new Date(occStart.getTime() + durationMs) });
    }
  }
  return out;
}

// ---- VEVENT / VCALENDAR -----------------------------------------------------

function statusToIcs(status?: string): string {
  if (status === 'canceled') return 'CANCELLED';
  return 'CONFIRMED';
}

/**
 * Build the VEVENT content lines for one occurrence. UID is stable per
 * (series, occurrence-start) so feed re-fetches don't duplicate or flicker;
 * a one-off uses the bare appointment id.
 */
export function buildVevent(
  appt: IcsAppointment,
  occ: Occurrence,
  opts: { method: string; dtstamp: Date },
): string[] {
  const isRecurring = Boolean(appt.rrule);
  const uid = isRecurring
    ? `${appt.id}-${fmtUtcBasic(occ.start)}@fitflex.app`
    : `${appt.id}@fitflex.app`;
  const summary = appt.title?.trim() || appt.attendeeName?.trim() || 'Appointment';

  const lines: string[] = [
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${fmtUtcBasic(opts.dtstamp)}`,
    `DTSTART:${fmtUtcBasic(occ.start)}`,
    `DTEND:${fmtUtcBasic(occ.end)}`,
    `SUMMARY:${escapeText(summary)}`,
    `STATUS:${statusToIcs(appt.status)}`,
  ];
  if (appt.location) lines.push(`LOCATION:${escapeText(appt.location)}`);
  if (appt.notes) lines.push(`DESCRIPTION:${escapeText(appt.notes)}`);
  // Invites (METHOD:REQUEST) carry organizer/attendee so the client can RSVP.
  if (opts.method === 'REQUEST' && appt.attendeeEmail) {
    const cn = appt.attendeeName ? `;CN=${escapeText(appt.attendeeName)}` : '';
    lines.push(`ATTENDEE;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE${cn}:mailto:${appt.attendeeEmail}`);
  }
  lines.push('END:VEVENT');
  return lines;
}

export interface BuildOptions {
  method?: string;          // 'PUBLISH' (feed/download) | 'REQUEST' (invite)
  windowStart?: Date;       // expansion horizon (default: 90 days back)
  windowEnd?: Date;         // expansion horizon (default: 367 days forward)
  now?: Date;               // DTSTAMP source (default: new Date())
}

/** Build a complete VCALENDAR document (CRLF-terminated) for the given appts. */
export function buildVcalendar(appts: IcsAppointment[], opts: BuildOptions = {}): string {
  const now = opts.now ?? new Date();
  const method = opts.method ?? 'PUBLISH';
  const winStart = opts.windowStart ?? new Date(now.getTime() - 90 * 86400000);
  const winEnd = opts.windowEnd ?? new Date(now.getTime() + 367 * 86400000);

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FitFlex//Coach Calendar//EN',
    'CALSCALE:GREGORIAN',
    `METHOD:${method}`,
  ];
  for (const appt of appts) {
    if (appt.status === 'canceled') continue; // a cancelled series is omitted entirely
    for (const occ of expandOccurrences(appt, winStart, winEnd)) {
      lines.push(...buildVevent(appt, occ, { method, dtstamp: now }));
    }
  }
  lines.push('END:VCALENDAR');
  return lines.map(foldLine).join('\r\n') + '\r\n';
}
